import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, Calendar, Phone, Loader2, Tablet, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => (iso || "—").slice(0, 16).replace("T", " ");

const THRESHOLDS = [
  { key: 3, label: "أكثر من 3 أيام" },
  { key: 7, label: "أكثر من أسبوع" },
  { key: 14, label: "أكثر من أسبوعين" },
  { key: 30, label: "أكثر من شهر" },
];

export default function StaleDebtsTab() {
  const [data, setData] = useState({ items: [], count: 0, total_debt: 0, days_threshold: 3 });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(3);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/stale-debts`, { params: { days } });
      setData(d);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);

  const severity = (d) => {
    if (d === null || d === undefined) return { c: "bg-slate-100 text-slate-700 border-slate-200" };
    if (d >= 30) return { c: "bg-red-100 text-red-800 border-red-300", icon: "🔴" };
    if (d >= 14) return { c: "bg-orange-100 text-orange-800 border-orange-300", icon: "🟠" };
    if (d >= 7) return { c: "bg-amber-100 text-amber-800 border-amber-300", icon: "🟡" };
    return { c: "bg-yellow-50 text-yellow-700 border-yellow-200", icon: "🟢" };
  };

  return (
    <div className="space-y-3" dir="rtl" data-testid="stale-debts-tab">
      <div className="bg-gradient-to-l from-[#DC2626] to-[#F59E0B] text-white rounded-2xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">تنبيهات الديون المتأخرة</h3>
              <p className="text-xs opacity-90">الزبائن اللي بذمتهم دين ومرّت أيام بدون دفع</p>
            </div>
          </div>
          <div className="text-left">
            <p className="text-[10px] opacity-80">إجمالي الدين المتأخر</p>
            <p className="text-xl font-black" data-testid="stale-total-debt" dir="ltr">{fmt(data.total_debt)} د.ع</p>
            <p className="text-[10px] opacity-80"><span data-testid="stale-count">{data.count}</span> زبون</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]" data-testid="stale-debts-ranges">
        <div className="flex flex-wrap gap-1.5">
          {THRESHOLDS.map((t) => (
            <button
              key={t.key}
              onClick={() => setDays(t.key)}
              data-testid={`stale-range-${t.key}`}
              className={`text-xs font-bold py-1.5 px-3 rounded-full transition-colors ${
                days === t.key ? "bg-[#DC2626] text-white" : "bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : data.items.length === 0 ? (
        <div className="bg-emerald-50 border-2 border-dashed border-emerald-300 rounded-2xl p-10 text-center text-emerald-700" data-testid="stale-empty">
          <AlertCircle className="w-10 h-10 mx-auto opacity-40 mb-2" />
          مكو زبون بدين متأخر — كلشي بايف ✅
        </div>
      ) : (
        <div className="space-y-1.5">
          {data.items.map((c) => {
            const sev = severity(c.days_since_last_payment);
            return (
              <div
                key={c.id}
                data-testid={`stale-row-${c.id}`}
                className={`bg-white rounded-xl p-3 flex items-center gap-2 border-2 ${sev.c.replace("text-", "border-").split(" ").pop()}`}
              >
                <span className="text-2xl">{sev.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-bold truncate">{c.market_name || c.full_name}</p>
                    {c.customer_id && (
                      <span className="bg-[#3B82F6]/15 text-[#1E3A5F] text-[9px] font-bold rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5">
                        <Tablet className="w-2.5 h-2.5" /> {c.customer_id}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#5C6E64] flex items-center gap-2 mt-0.5">
                    <Phone className="w-2.5 h-2.5" />
                    <span dir="ltr">{c.phone}</span>
                    <span className="mx-1">·</span>
                    <Calendar className="w-2.5 h-2.5" />
                    <span>آخر دفعة: <span dir="ltr">{fmtDate(c.last_payment_at)}</span></span>
                  </p>
                </div>
                <div className="text-left shrink-0">
                  <p className="text-[10px] text-[#5C6E64]">الدين</p>
                  <p className="font-black text-[#DC2626]" dir="ltr">{fmt(c.customer_debt)}</p>
                  <p className={`text-[10px] font-bold ${sev.c.split(" ")[1]}`} dir="ltr">
                    {c.days_since_last_payment === null ? "—" : `${c.days_since_last_payment} يوم`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
